/**
 * Shared date styles used by friendly date/timestamp renderers.
 * `default` is the long weekday style, while `compact` is the due-date style.
 *
 * Visual examples:
 * - `default` + within year: "Wednesday, Dec 31"
 * - `default` + cross year: "Wednesday, Dec 31, 2025"
 * - `compact` + within year: "31 Dec"
 * - `compact` + cross year: "31 Dec 2025"
 */
export const FRIENDLY_DATE_FORMAT_STYLES = {
    DEFAULT: 'default',
    COMPACT: 'compact',
} as const;

export type FriendlyDateFormatStyle = (typeof FRIENDLY_DATE_FORMAT_STYLES)[keyof typeof FRIENDLY_DATE_FORMAT_STYLES];

type FriendlyDateFormatPreset = {
    withinYear: Intl.DateTimeFormatOptions;
    crossYear: Intl.DateTimeFormatOptions;
};

/**
 * Centralized Intl options so different features render the same date style.
 *
 * `withinYear`: Used when the target date is in the same year as the reference date.
 * `crossYear`: Used when the target date is in a different year.
 */
const FRIENDLY_DATE_FORMAT_PRESETS: Record<FriendlyDateFormatStyle, FriendlyDateFormatPreset> = {
    [FRIENDLY_DATE_FORMAT_STYLES.DEFAULT]: {
        withinYear: {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
        },
        crossYear: {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        },
    },
    [FRIENDLY_DATE_FORMAT_STYLES.COMPACT]: {
        withinYear: {
            day: 'numeric',
            month: 'short',
        },
        crossYear: {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        },
    },
};

/**
 * Absolute date fallback used by friendly relative-time helpers (> 4 weeks).
 */
export const FRIENDLY_TIME_ABSOLUTE_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
};

/**
 * Full date+time tooltip style used in context menus.
 */
export const FULL_TIMESTAMP_TOOLTIP_OPTIONS: Intl.DateTimeFormatOptions = {
    dateStyle: 'medium',
    timeStyle: 'short',
};

/**
 * Example output: "Friday, 15 March 2024".
 * Use when a full date with weekday is needed.
 */
export const FULL_DATE_WITH_WEEKDAY_OPTIONS: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
};

/**
 * Example output: "15 March 2024".
 * Use when a long date without weekday is needed.
 */
export const LONG_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
};

export const CALENDAR_DATE_FORMAT_PRESETS = {
    LONG_WITH_WEEKDAY: 'long_with_weekday',
    LONG: 'long',
} as const;

export type CalendarDateFormatPreset = (typeof CALENDAR_DATE_FORMAT_PRESETS)[keyof typeof CALENDAR_DATE_FORMAT_PRESETS];

const CALENDAR_DATE_PRESET_OPTIONS: Record<CalendarDateFormatPreset, Intl.DateTimeFormatOptions> = {
    [CALENDAR_DATE_FORMAT_PRESETS.LONG_WITH_WEEKDAY]: FULL_DATE_WITH_WEEKDAY_OPTIONS,
    [CALENDAR_DATE_FORMAT_PRESETS.LONG]: LONG_DATE_OPTIONS,
};

export const getCalendarDateFormatOptions = ({
    preset,
}: {
    preset: CalendarDateFormatPreset;
}): Intl.DateTimeFormatOptions => CALENDAR_DATE_PRESET_OPTIONS[preset];

/**
 * Returns the `withinYear` or `crossYear` preset for the requested style.
 */
export const getFriendlyDateFormatOptions = ({
    style = FRIENDLY_DATE_FORMAT_STYLES.DEFAULT,
    isWithinCurrentYear,
}: {
    style?: FriendlyDateFormatStyle;
    isWithinCurrentYear: boolean;
}): Intl.DateTimeFormatOptions => {
    const preset = FRIENDLY_DATE_FORMAT_PRESETS[style];

    return isWithinCurrentYear ? preset.withinYear : preset.crossYear;
};
